import { useEffect, useRef } from 'react';
import {
  OPEN_START_FRACTION,
  classifyEdgeSwipe,
  hasTouchCapability,
  isGestureBlockedTarget,
  isMenuSwipeLocked,
  isOverlayBlockingMenuSwipe,
  type TouchPoint,
} from '../utils/edgeSwipeMenu';

type Options = {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  /** When false, listeners are not attached (e.g. login screen). Default true. */
  enabled?: boolean;
};

/**
 * Touch-capable devices: right swipe from the left ~40% of the screen opens
 * the nav drawer; left swipe closes it. Passive listeners — we never
 * preventDefault, so vertical scroll, sideways tables, and system Back keep
 * working. Gated on touch capability, not CSS max-width alone.
 */
export function useEdgeSwipeMenu({ menuOpen, setMenuOpen, enabled = true }: Options): void {
  const menuOpenRef = useRef(menuOpen);
  menuOpenRef.current = menuOpen;
  const setMenuOpenRef = useRef(setMenuOpen);
  setMenuOpenRef.current = setMenuOpen;
  const startRef = useRef<TouchPoint | null>(null);
  const trackingRef = useRef(false);
  const committedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (!hasTouchCapability()) return;

    const reset = () => {
      startRef.current = null;
      trackingRef.current = false;
      committedRef.current = false;
    };

    const tryCommit = (end: TouchPoint) => {
      if (committedRef.current || !startRef.current) return;
      // Drag may start after touchstart (FV long-press); re-check on every move.
      if (isMenuSwipeLocked() || isOverlayBlockingMenuSwipe()) return;
      const vw =
        typeof window !== 'undefined'
          ? window.innerWidth || document.documentElement.clientWidth || 0
          : 0;
      const action = classifyEdgeSwipe(startRef.current, end, menuOpenRef.current, vw);
      if (action === 'open') {
        committedRef.current = true;
        setMenuOpenRef.current(true);
      } else if (action === 'close') {
        committedRef.current = true;
        setMenuOpenRef.current(false);
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      reset();
      if (e.touches.length !== 1) return;
      if (isMenuSwipeLocked() || isOverlayBlockingMenuSwipe()) return;

      const t = e.touches[0];
      if (isGestureBlockedTarget(e.target)) return;

      const point = { clientX: t.clientX, clientY: t.clientY };
      if (!menuOpenRef.current) {
        const vw = window.innerWidth || document.documentElement.clientWidth || 0;
        // Only track potential open swipes that begin in the left band.
        if (t.clientX > vw * OPEN_START_FRACTION) return;
      }
      startRef.current = point;
      trackingRef.current = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!trackingRef.current || !startRef.current || committedRef.current) return;
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      tryCommit({ clientX: t.clientX, clientY: t.clientY });
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!trackingRef.current || !startRef.current) {
        reset();
        return;
      }
      const t = e.changedTouches[0];
      if (t && !committedRef.current) {
        tryCommit({ clientX: t.clientX, clientY: t.clientY });
      }
      reset();
    };

    const onTouchCancel = () => {
      reset();
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
      reset();
    };
  }, [enabled]);
}
